import i18next, { DEFAULT_APP_LANGUAGE } from "@app/i18n"
import { useUserSettingsStore } from "@shared/stores"
import { useEffect } from "react"
import { Outlet } from "react-router-dom"

/**
 * The MRV flow is English-only.
 *
 * MRV pages don't only translate via the `useTranslation` hook — many shared
 * utilities (unit/country/number mappers, Zustand stores building toasts, table
 * column configs) call the global i18next singleton directly (`i18next.t(...)`).
 * A scoped React `I18nextProvider` cannot reach those calls, so the only
 * reliable way to keep the whole MRV flow in English is to pin the global
 * i18next instance to en-US while any MRV route is mounted, and restore the
 * user's selected language when they leave the flow.
 */
export const MrvLocaleLayout: React.FC = () => {
	useEffect(() => {
		const forceEnglish = (lng: string) => {
			if (lng !== DEFAULT_APP_LANGUAGE) {
				i18next.changeLanguage(DEFAULT_APP_LANGUAGE)
			}
		}

		// Pin to English on entry and keep it pinned if anything (e.g. the
		// language selector) tries to change the language while in MRV.
		forceEnglish(i18next.language)
		i18next.on("languageChanged", forceEnglish)

		return () => {
			i18next.off("languageChanged", forceEnglish)
			const userLanguage =
				useUserSettingsStore.getState().currentLanguage ||
				DEFAULT_APP_LANGUAGE
			i18next.changeLanguage(userLanguage)
		}
	}, [])

	return <Outlet />
}
